import React, { useState } from 'react';
import { Button } from './ui/Button';
import { Card } from './ui/Card';
import * as FiIcons from 'react-icons/fi';

const { FiUser } = FiIcons;

export const CheckInFlow = ({ guest, onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({
    relationship_type: guest.relationship?.relationship_type || '',
    booked_at_padua: guest.relationship?.booked_at_padua || false,
    tour_status: guest.relationship?.tour_status || 'Never Toured',
    arrived_guest_count: guest.guest_count || 1
  });

  const handleAnswer = (key, value, nextStep) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    setStep(nextStep);
  };

  const finishFlow = () => {
    onComplete(answers);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white shadow-2xl relative animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="bg-padua-green p-6 text-center rounded-t-2xl relative">
          <button onClick={onCancel} className="absolute left-4 top-4 text-white/70 hover:text-white text-sm font-medium">Cancel</button>
          <div className="w-16 h-16 bg-padua-gold rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
            <FiUser className="text-white text-3xl" />
          </div>
          <h2 className="text-2xl font-cinzel text-white">{guest.contact.first_name} {guest.contact.last_name}</h2>
          <p className="text-padua-ivory/80 mt-1">{guest.rsvp_source}</p>
        </div>

        <div className="p-8">
          {step === 1 && (
            <div className="space-y-6 text-center">
              <h3 className="text-2xl font-medium text-padua-green mb-8">Are you currently booked with Padua Weddings?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button variant="large" onClick={() => handleAnswer('booked_at_padua', true, 5)}>
                  Yes — Booked
                </Button>
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('booked_at_padua', false, 2)}>
                  No
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6 text-center">
              <h3 className="text-2xl font-medium text-padua-green mb-8">Are you currently planning a wedding?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button variant="large" onClick={() => handleAnswer('relationship_type', 'Prospective Couple', 3)}>
                  Yes
                </Button>
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('relationship_type', 'Guest / Other', 4)}>
                  No
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 text-center">
              <h3 className="text-2xl font-medium text-padua-green mb-8">Have you toured Padua before?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('tour_status', 'Never Toured', 5)}>
                  Never Toured
                </Button>
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('tour_status', 'Tour Scheduled', 5)}>
                  Tour Scheduled
                </Button>
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('tour_status', 'Previously Toured', 5)}>
                  Previously Toured
                </Button>
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('tour_status', 'Unknown', 5)}>
                  Not Sure
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6 text-center">
              <h3 className="text-2xl font-medium text-padua-green mb-8">Guest Type</h3>
              <div className="grid grid-cols-1 gap-4 max-w-sm mx-auto">
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('relationship_type', 'Guest / Family / Friend', 5)}>
                  Guest / Family / Friend
                </Button>
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('relationship_type', 'Vendor', 5)}>
                  Vendor
                </Button>
                <Button variant="outline" className="text-lg py-4 px-8 rounded-xl" onClick={() => handleAnswer('relationship_type', 'Other', 5)}>
                  Other
                </Button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6 text-center animate-in fade-in">
              <h3 className="text-2xl font-medium text-padua-green mb-8">How many guests are in your party today?</h3>
              <div className="flex items-center justify-center space-x-8 mb-6">
                <button 
                  onClick={() => setAnswers(prev => ({ ...prev, arrived_guest_count: Math.max(1, prev.arrived_guest_count - 1) }))}
                  className="w-16 h-16 rounded-full border-2 border-padua-green text-padua-green text-3xl flex items-center justify-center hover:bg-padua-green/5 transition-colors"
                >
                  -
                </button>
                <span className="text-5xl font-cinzel text-padua-green w-16 text-center">
                  {answers.arrived_guest_count}
                </span>
                <button 
                  onClick={() => setAnswers(prev => ({ ...prev, arrived_guest_count: prev.arrived_guest_count + 1 }))}
                  className="w-16 h-16 rounded-full bg-padua-green text-white text-3xl flex items-center justify-center hover:bg-padua-green/90 transition-colors"
                >
                  +
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-8">Include yourself in this total count.</p>
              <Button variant="large" className="w-full max-w-md mx-auto" onClick={() => setStep('ready')}>
                Continue
              </Button>
            </div>
          )}

          {step === 'ready' && (
            <div className="space-y-8 text-center animate-in fade-in">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Ready to Check In</h3>
              <div className="text-3xl font-cinzel text-padua-green mb-1">{guest.contact.first_name} {guest.contact.last_name}</div>
              <div className="text-lg font-medium text-padua-gold mb-8">Party of {answers.arrived_guest_count}</div>
              <Button variant="large" className="w-full max-w-md mx-auto py-5 text-xl" onClick={finishFlow}>
                COMPLETE CHECK-IN
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};